// Iter 82.7 / Phase E2.3 — Attendance Manager Workspace.
// Action-oriented landing page for managers featuring the operator-specified
// Team Action Queue (Pending Regularizations · Critical Anomalies ·
// Employees Absent Today · Employees Late Today) + team status counts +
// MTD KPIs + upcoming team leaves.
// Powered by /api/attendance/dashboard/manager.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    UsersThree, FileText, Warning, UserMinus, Hourglass,
    CalendarCheck, ChartLineUp, CheckCircle, XCircle, Sun,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

function StatCard({ icon, label, value, tone = "slate", testid, to }) {
    const map = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        violet: "bg-violet-50 border-violet-200 text-violet-900",
        orange: "bg-orange-50 border-orange-200 text-orange-900",
    };
    const card = (
        <div className={`rounded-xl border p-4 ${map[tone]} ${to ? "hover:shadow transition cursor-pointer" : ""}`} data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
                {icon}
            </div>
            <div className="text-3xl font-bold mt-2">{value}</div>
        </div>
    );
    return to ? <Link to={to}>{card}</Link> : card;
}

export default function AttendanceManagerWorkspace() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const { data: payload } = await api.get("/attendance/dashboard/manager");
            setData(payload);
        } catch (err) {
            const msg = formatApiError(err, "Failed to load manager workspace");
            setLoadError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { Promise.resolve().then(reload); }, [reload]);

    if (loading) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="att-mgr-loading">
                <PageHeader eyebrow="Attendance" title="Manager Workspace" />
                <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>
            </div>
        );
    }

    // UAT-03 (2026-07-14) — Previously this guard was
    // `if (loading || !data)` which produced an infinite Loading… spinner
    // whenever the backend returned a 403 / 404 / 500 (payload never
    // arrived, but `loading` flipped to false in the `finally` block).
    // Managers holding only `manager:read` are now admitted at the
    // backend (see routes_attendance_reports.py:1403), so 403 no longer
    // fires, but the defensive empty-state remains for future-proofing:
    // any transport-level failure now renders an actionable message
    // + retry button instead of an infinite loader.
    if (!data) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="att-mgr-empty">
                <PageHeader eyebrow="Attendance" title="Manager Workspace" />
                <div className="mt-6 rounded-xl border border-slate-200 bg-white px-6 py-10 text-center">
                    <Warning size={32} weight="duotone" className="text-amber-500 mx-auto mb-3" />
                    <div className="text-sm font-medium text-slate-800" data-testid="att-mgr-error-msg">
                        {loadError || "The manager workspace payload could not be loaded."}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        This surface is only available to managers with active direct reports or HR administrators. If you believe this is a mistake, contact your HR team.
                    </div>
                    <Button size="sm" variant="outline" className="mt-4" onClick={reload} data-testid="att-mgr-retry-btn">
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    const t = data.today_status_counts || {};
    const taq = data.team_action_queue || {};

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="att-mgr-page">
            <PageHeader
                eyebrow="Attendance"
                title="Manager Workspace"
                description={`${data.direct_reports_count} direct report${data.direct_reports_count === 1 ? "" : "s"} · MTD team attendance ${(data.team_attendance_pct_mtd ?? 0).toFixed(1)}%${data.scope === "organization_via_admin" ? " · Org-wide (HR mode)" : ""}`}
                actions={(
                    <div className="flex gap-2">
                        <Link to="/regularization/requests?scope=team">
                            <Button variant="outline" size="sm" data-testid="att-mgr-regs-btn">
                                <FileText size={14} className="mr-1" /> Regularizations
                            </Button>
                        </Link>
                        <Link to="/attendance/anomalies?scope=team">
                            <Button size="sm" data-testid="att-mgr-anomalies-btn">
                                <Warning size={14} className="mr-1" /> Team Anomalies
                            </Button>
                        </Link>
                    </div>
                )}
            />

            {/* Team Action Queue — operator-specified */}
            <section className="mt-5" data-testid="att-mgr-team-action-queue">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Team Action Queue</div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <StatCard testid="taq-pending-regs" icon={<FileText size={18} />} label="Pending Regularizations" value={taq.pending_regularizations_count || 0} tone="amber" to="/regularization/requests?scope=team&status_filter=Pending" />
                    <StatCard testid="taq-critical-anomalies" icon={<Warning size={18} />} label="Critical Anomalies" value={taq.critical_anomalies_count || 0} tone="rose" to="/attendance/anomalies?scope=team&severity=Critical" />
                    <StatCard testid="taq-absent-today" icon={<UserMinus size={18} />} label="Employees Absent Today" value={taq.absent_today_count || 0} tone="orange" to={`/admin/attendance/snapshots?from_date=${data.today}&to_date=${data.today}&attendance_status=Absent`} />
                    <StatCard testid="taq-late-today" icon={<Hourglass size={18} />} label="Employees Late Today" value={taq.late_today_count || 0} tone="amber" to={`/admin/attendance/snapshots?from_date=${data.today}&to_date=${data.today}`} />
                </div>
            </section>

            {/* Team status today */}
            <section className="mt-5" data-testid="att-mgr-today-status">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Team Status · Today ({formatDate(data.today)})</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard testid="status-present" icon={<CheckCircle size={18} />} label="Present" value={t.Present || 0} tone="emerald" />
                    <StatCard testid="status-absent" icon={<XCircle size={18} />} label="Absent" value={t.Absent || 0} tone="rose" />
                    <StatCard testid="status-halfday" icon={<Hourglass size={18} />} label="Half Day" value={t["Half Day"] || 0} tone="amber" />
                    <StatCard testid="status-onleave" icon={<Sun size={18} />} label="On Leave" value={t["On Leave"] || 0} tone="violet" />
                    <StatCard testid="status-holiday" icon={<CalendarCheck size={18} />} label="Holiday" value={t.Holiday || 0} tone="sky" />
                    <StatCard testid="status-weeklyoff" icon={<CalendarCheck size={18} />} label="Weekly Off" value={t["Weekly Off"] || 0} tone="slate" />
                </div>
            </section>

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Absent today list */}
                <section className="bg-white rounded-xl border border-slate-200" data-testid="att-mgr-absent-list">
                    <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <UserMinus size={14} /> Absent Today ({taq.absent_today_count || 0})
                        </h3>
                    </div>
                    {(!taq.absent_today || taq.absent_today.length === 0) ? (
                        <div className="text-center py-8 text-sm text-slate-400">Nobody absent today.</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {taq.absent_today.slice(0, 10).map((e) => (
                                <li key={e.employee_id} className="px-5 py-2.5 flex items-center justify-between" data-testid={`absent-row-${e.employee_id}`}>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-800">{e.employee_name}</div>
                                        <div className="text-xs text-slate-500">{e.employee_code} · {e.department || "—"}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Late today list */}
                <section className="bg-white rounded-xl border border-slate-200" data-testid="att-mgr-late-list">
                    <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <Hourglass size={14} /> Late Today ({taq.late_today_count || 0})
                        </h3>
                    </div>
                    {(!taq.late_today || taq.late_today.length === 0) ? (
                        <div className="text-center py-8 text-sm text-slate-400">Nobody late today.</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {taq.late_today.slice(0, 10).map((e) => (
                                <li key={e.employee_id} className="px-5 py-2.5 flex items-center justify-between" data-testid={`late-row-${e.employee_id}`}>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-800">{e.employee_name}</div>
                                        <div className="text-xs text-slate-500">{e.employee_code} · {e.department || "—"}</div>
                                    </div>
                                    <Badge className="bg-amber-100 text-amber-800 text-xs">+{e.late_minutes}m</Badge>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Pending Regularizations awaiting me */}
                <section className="bg-white rounded-xl border border-slate-200" data-testid="att-mgr-pending-regs">
                    <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <FileText size={14} /> Pending Regularizations ({data.pending_regularizations_count})
                        </h3>
                        <Link to="/regularization/requests?scope=team&status_filter=Pending" className="text-xs text-slate-500 hover:underline" data-testid="att-mgr-pending-link">View all</Link>
                    </div>
                    {data.pending_regularizations.length === 0 ? (
                        <div className="text-center py-8 text-sm text-slate-400">Nothing pending your approval.</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {data.pending_regularizations.slice(0, 5).map((r) => (
                                <li key={r.id} className="px-5 py-3 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => window.location.assign(`/regularization/requests?scope=team&status_filter=Pending`)}
                                    data-testid={`pending-reg-${r.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-semibold text-slate-800">{r.request_type.replaceAll("_", " ")}</div>
                                        <Badge className="bg-amber-100 text-amber-800 text-xs">{r.status}</Badge>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">Employee · {r.employee_id} · {formatDate(r.attendance_date)}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Critical anomalies */}
                <section className="bg-white rounded-xl border border-slate-200" data-testid="att-mgr-critical-anomalies">
                    <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <Warning size={14} /> Critical Anomalies (last 7 days)
                        </h3>
                        <Link to="/attendance/anomalies?scope=team&severity=Critical" className="text-xs text-slate-500 hover:underline" data-testid="att-mgr-critical-link">View all</Link>
                    </div>
                    {(!taq.critical_anomalies || taq.critical_anomalies.length === 0) ? (
                        <div className="text-center py-8 text-sm text-slate-400">No critical anomalies. Healthy team!</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {taq.critical_anomalies.slice(0, 5).map((a) => (
                                <li key={a.id} className="px-5 py-3 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => window.location.assign(`/attendance/anomalies?scope=team`)}
                                    data-testid={`critical-anomaly-${a.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-semibold text-slate-800">{a.anomaly_type.replaceAll("_", " ")}</div>
                                        <Badge className="bg-rose-100 text-rose-800 text-xs">Critical</Badge>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">Employee · {a.employee_id} · {formatDate(a.attendance_date)}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            {/* Upcoming team leaves */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="att-mgr-upcoming-leaves">
                <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                        <Sun size={14} /> Upcoming Team Leaves (next 14 days)
                    </h3>
                    <Link to="/leave/calendar" className="text-xs text-slate-500 hover:underline" data-testid="att-mgr-cal-link">Team calendar</Link>
                </div>
                {(!data.upcoming_team_leaves || data.upcoming_team_leaves.length === 0) ? (
                    <div className="text-center py-8 text-sm text-slate-400">No approved upcoming leaves.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Leave Type</th>
                                <th className="text-left px-3 py-2 font-medium">From → To</th>
                                <th className="text-right px-3 py-2 font-medium">Days</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.upcoming_team_leaves.map((l) => (
                                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`upcoming-leave-${l.id}`}>
                                    <td className="px-3 py-2">{l.employee_name || l.employee_code}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{l.leave_type_code}</td>
                                    <td className="px-3 py-2 text-xs">{formatDate(l.from_date)} → {formatDate(l.to_date)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{Number(l.days_total).toFixed(1)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}
